<template>
    <b-card title="Invoice details">
        <b-row>
            <b-col>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.InvoiceDate')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <Datepicker input-class="form-control form-control-sm" :monday-first="true"
                                :class="{ 'is-invalid': v.invoiceDate.$error }" :typeable="false"
                                v-model="invoiceDetails.invoiceDate" @update="v.invoiceDate.$touch()"
                                name="invoiceDate" />
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                    </b-input-group>
                    <validationField :fieldName="'Invoice date'" v-if="v.invoiceDate.$dirty" :validator="v.invoiceDate">
                    </validationField>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.InvoiceNumber')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <b-form-input v-model="invoiceDetails.invoiceNumber" class="form-control form-control-sm"
                                name="invoiceNumber"></b-form-input>
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'InvoiceNumber'" v-if="v.invoiceNumber.$dirty"
                            :validator="v.invoiceNumber"></validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" label-align-sm="left" :label="$t('createInvoice.label.AwbNumber')">
                    <b-col sm="10">
                        <b-form-input v-model="invoiceDetails.awbNumber" class="form-control form-control-sm"
                            name="awbNumber"></b-form-input>
                    </b-col>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.TransportProduct')"
                    label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <vSelect :dynamicWidth="true" v-model="invoiceDetails.transportProduct"
                                :placeholder="$t('createInvoice.placeholder.ChooseTransportProduct')"
                                :options="getListOfTransportProducts" label="displayName"
                                :class="{ 'is-invalid': v.transportProduct.$error }" @update="v.transportProduct"
                                name="transportProduct" />
                        </b-col>
                        <validationField :fieldName="'Transport product'" v-if="v.transportProduct.$dirty"
                            :validator="v.transportProduct"></validationField>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.Incoterms')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <vSelect :dynamicWidth="true" v-model="invoiceDetails.incoterm"
                                :placeholder="$t('createInvoice.placeholder.ChooseIncoterm')"
                                :options="getListOfIncoterms" label="incotermName"
                                :reduce="(incoterm) => incoterm.incotermName"
                                :class="{ 'is-invalid': v.incoterm.$error }" @update="v.incoterm.$touch()"
                                name="incoterm" />
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Incoterm'" v-if="v.incoterm.$dirty" :validator="v.incoterm">
                        </validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.ExportReason')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <vSelect :dynamicWidth="true" v-model="invoiceDetails.exportReason"
                                :placeholder="$t('createInvoice.placeholder.ChooseExportreason')"
                                :options="getExportReasons"
                                :reduce="(exportReason) => exportReason.exportReasonCodeName"
                                label="exportReasonCodeName" :class="{ 'is-invalid': v.exportReason.$error }"
                                @update="v.exportReason.$touch()" name="exportReason" />
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Export reason'" v-if="v.exportReason.$dirty"
                            :validator="v.exportReason"></validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.Valuta')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <vSelect :dynamicWidth="true" v-model="invoiceDetails.valuta"
                                :placeholder="$t('createInvoice.placeholder.ChooseValuta')" :options="getCurrencies"
                                label="currencyName" name="currencyName" :reduce="(currency) => currency.currencyCode"
                                :class="{ 'is-invalid': v.valuta.$error }" @update="v.valuta.$touch()" />
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Valuta'" v-if="v.valuta.$dirty" :validator="v.valuta">
                        </validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.InvoiceType')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <vSelect :dynamicWidth="true" v-model="invoiceDetails.invoiceType"
                                :placeholder="$t('createInvoice.placeholder.ChooseInvoiceType')" :options="invoiceTypes"
                                label="invoiceTypeName" name="invoiceTypeName"
                                :reduce="(invoice) => invoice.invoiceTypeId"
                                :class="{ 'is-invalid': v.invoiceType.$error }" @update="v.invoiceType.$touch()" />
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Invoice type'" v-if="v.invoiceType.$dirty"
                            :validator="v.invoiceType"></validationField>
                    </b-input-group>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.TotalWeight')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <b-form-input v-model="invoiceDetails.totalWeight" name="totalWeight"
                                class="form-control form-control-sm" :class="{ 'is-invalid': v.totalWeight.$error }"
                                @update="v.totalWeight.$touch()"></b-form-input>
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Total weight'" v-if="v.totalWeight.$dirty"
                            :validator="v.totalWeight"></validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('history.editInvoiceDetails.label.numberOfColli')" label-align-sm="left">
                    <b-input-group>
                        <b-col sm="10">
                            <b-form-input v-model="invoiceDetails.colliCount" name="numberofColli"
                                class="form-control form-control-sm" :class="{ 'is-invalid': v.colliCount.$error }"
                                @update="v.colliCount.$touch()"></b-form-input>
                        </b-col>
                        <b-input-group-append>
                            <font-awesome-icon icon="exclamation-circle" size="lg" />
                        </b-input-group-append>
                        <validationField :fieldName="'Colli count'" v-if="v.colliCount.$dirty"
                            :validator="v.colliCount"></validationField>
                    </b-input-group>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.Declaration')" label-align-sm="left">
                    <b-col sm="10">
                        <b-form-checkbox name="exportDeclaration"
                            v-model="invoiceDetails.exportDeclaration"></b-form-checkbox>
                    </b-col>
                </b-form-group>
                <b-form-group label-cols-sm="4" :label="$t('createInvoice.label.Remarks')" label-align-sm="left">
                    <b-col sm="10">
                        <b-form-textarea id="textarea" v-model="invoiceDetails.remarks" placeholder="" rows="2"
                            max-rows="4" style="font-size: 12px; font-weight: 700"></b-form-textarea>
                    </b-col>
                </b-form-group>
            </b-col>
        </b-row>
    </b-card>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from '../vSelect';
import validationField from '../Validation/ValidationField';
import Datepicker from 'vuejs-datepicker';

export default {
    components: {
        vSelect,
        validationField,
        Datepicker,
    },
    props: {
        v: { required: true },
        invoiceDetails: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            invoiceTypes: [
                {
                    invoiceTypeName: 'Invoice',
                    invoiceTypeId: 0,
                },
                {
                    invoiceTypeName: 'Proforma',
                    invoiceTypeId: 1,
                },
            ],
        };
    },
    created() {
        if (this.invoiceDetails.colliCount === undefined || this.invoiceDetails.colliCount === null) {
            this.$set(this.invoiceDetails, 'colliCount', 0);
        }
        if (this.v.colliCount === undefined || this.v.colliCount === null) {
            this.$set(this.v, 'colliCount', 0);
        }
    },
    computed: {
        ...mapGetters('core', [
            'getListOfTransportProducts',
            'getListOfIncoterms',
            'getExportReasons',
            'getCurrencies',
        ]),
    },
};
</script>

<style></style>
